import { useUserStore } from '@/stores/user';
import type { RouteLocationNormalized as Route, NavigationGuardNext as Next, RouteLocation, LocationQuery, LocationQueryRaw } from 'vue-router';
import { dialogService } from '@/services/dialog.service';
import { useFCM } from '@/composables/useFCM';
import type { Profile } from '@amministro-io-packages/auth-interfaces';
import type { OneOf } from '@/types/OneOf';
import { pick } from '@/lib/pick';
import { filterEntries } from '@/lib/filterEntries';
import { exclude } from '@/lib/exclude';

type HomeQueryToStrategy = OneOf<
  [{ to: string }, { to_interface: 'interfaccia_amministroio' | 'interfaccia_archivioplus' | 'interfaccia_notificoonline'; to_path: string }]
>;

type HomeQueryProfileStrategy = OneOf<
  [
    {
      /* profile_uid actually refers to the xAmministroProfileHeader */
      profile_uid: string;
    },
    { company_id: string; company_type: Profile['company_type']; role?: Profile['role'] },
    { profile_id: string },
  ]
>;

export type HomeQuery = (HomeQueryToStrategy & HomeQueryProfileStrategy) | Record<string, never>;

function extractHomeQueryParams({ query }: Route): LocationQuery {
  return filterEntries(pick(query, ['to', 'to_interface', 'to_path', 'profile_uid', 'company_id', 'company_type', 'role', 'profile_id']), ([, value]) =>
    Boolean(value),
  ) as LocationQuery;
}

/**
 * Sospende la navigazione (reindirizzamento in Check) se l'autenticazione è in corso,
 * prosegue altrimenti
 * @param to: rotta di destinazione
 * @param _from: rotta di partenza
 * @param next: next handler
 */
export function isLoading(to: Route, _from: Route, next: Next): void {
  const userStore = useUserStore();

  const isStillLoading = userStore.pending;
  const isSicuraSolution = to.redirectedFrom?.fullPath.includes('sicura-solution') || to.fullPath.includes('sicura-solution');

  if (isStillLoading) {
    const { query, params, name: back } = to;

    const name = isSicuraSolution ? 'SicuraSolutionCheck' : 'Check';
    const homeName = isSicuraSolution ? 'SicuraSolutionEntryPoint' : 'Home';
    const checkBackName = back === homeName;
    const backName = isSicuraSolution ? 'SicuraSolutionLauncher' : 'Launcher';

    console.info(name, homeName, backName);

    next({
      name,
      query: {
        ...query,
        back: checkBackName ? backName : (back as string),
        params: encodeURIComponent(JSON.stringify(params)),
      },
    });
  }
  else {
    next();
  }
}

/**
 * Blocca la navigazione (reindirizzamento in Home) se l'utente non è autenticato,
 * prosegue altrimenti
 * @param _to: rotta di destinazione
 * @param _from: rotta di partenza
 * @param next: next handler
 */
export function isAuthenticated(to: Route, from: Route, next: Next): void {
  const userStore = useUserStore();

  const userIsAuthenticated = userStore.user !== null && userStore.user !== undefined;

  if (userIsAuthenticated) {
    next();
  }
  else {
    const name = from.fullPath?.includes('sicura-solution') ? 'SicuraSolutionCheck' : 'Check';
    if (from.name === name) {
      const { query, params, name: back } = to;

      const loginQuery: LocationQueryRaw = {
        ...extractHomeQueryParams(to),
        ...exclude(query, ['to', 'to_interface', 'to_path', 'profile_uid', 'company_id', 'company_type', 'role', 'profile_id']),
        ...(back ? { back: back as string } : {}),
        params: encodeURIComponent(JSON.stringify(params)),
      };

      const name = from.fullPath?.includes('sicura-solution') ? 'SicuraSolutionLogin' : 'Login';
      next({ name, query: loginQuery });
    }
    else {
      next(false);
    }
  }
}

export function isNotAuthenticated(to: Route, from: Route, next: Next): void {
  const userStore = useUserStore();

  const userIsAuthenticated = userStore.user !== null && userStore.user !== undefined;

  if (!userIsAuthenticated) {
    next();
  }
  else {
    const name = from.fullPath?.includes('sicura-solution') ? 'SicuraSolutionCheck' : 'Check';
    if (from.name === name) {
      const name = from.fullPath?.includes('sicura-solution') ? 'SicuraSolutionEntryPoint' : 'Home';
      next({
        name,
        query: extractHomeQueryParams(to),
      });
    }
    else {
      next(false);
    }
  }
}

function preventIfAuthenticated(onPrevent: (to: Route, from: Route) => Promise<RouteLocation>) {
  return async function (to: Route, from: Route) {
    const userStore = useUserStore();
    const userIsAuthenticated = userStore.user !== null && userStore.user !== undefined;

    if (!userIsAuthenticated) {
      return true;
    }
    else {
      return onPrevent(to, from);
    }
  };
}

const logout = () => {
  const userStore = useUserStore();
  const { stopListeningForNotifications } = useFCM();

  return stopListeningForNotifications()
    .catch(() => {
      /** do nothing */
    })
    .then(() => userStore.signOut(true));
};

export const preventLoginMagicLinkIfAuthenticated = preventIfAuthenticated(async function onPrevent(to: Route) {
  try {
    await dialogService.showDialog({
      title: 'Attenzione',
      description: 'Non è possibile accedere attraverso un link se se hai già effettuato l\'accesso',
      successButtonText: 'Logout',
      errorButtonText: 'Chiudi',
    });
    await logout();
    const name = to.fullPath?.includes('sicura-solution') ? 'SicuraSolutionLogin' : 'Login';
    return { name } as RouteLocation;
  }
  catch {
    const name = to.fullPath?.includes('sicura-solution') ? 'SicuraSolutionEntryPoint' : 'Home';
    return {
      name,
      query: extractHomeQueryParams(to),
    } as RouteLocation;
  }
  finally {
    dialogService.closeDialog();
  }
});

export const redirectNotificationsCenter = (_to: Route, from: Route, next: Next): void => {
  const { query } = from;
  if (Number.parseInt(query['noti'] as string) === 1) {
    delete query['noti'];
    const name = from.fullPath?.includes('sicura-solution') ? 'SicuraSolutionNotificationsCenter' : 'NotificationsCenter';
    next({ name, query });
  }
  else next();
};
